import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import './articleDetails.css';
import LazyLoad from 'react-lazyload';
import BlurhashImg from '../../@/components/Image/BlurhashImg';
import { FaFacebook, FaTwitter, FaInstagram, FaPinterest, FaCopy } from 'react-icons/fa';


const incrementShareCount = async (postId) => {
  try {
    const postRef = doc(db, 'news', postId);
    await updateDoc(postRef, { 
      shareCount: increment(1) 
    });
  } catch (error) {
    console.error("Error incrementing share count:", error);
  }
};


const ArticleDetails = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePopupPostId, setActivePopupPostId] = useState(null);
  const [hoverPostId, setHoverPostId] = useState(null);
  const [localShareCounts, setLocalShareCounts] = useState({});
  const [copiedPostId, setCopiedPostId] = useState(null);
  const shareRefsMap = useRef({});
  const { id } = useParams();
  const videoRefs = useRef({});
  const [playingVideos, setPlayingVideos] = useState({});

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const docRef = doc(db, 'news', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setArticle({ id: docSnap.id, ...docSnap.data() });
        } else {
          throw new Error('No article found for this ID!');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch((error) => console.error('Error playing video:', error));
            setPlayingVideos((prev) => ({ ...prev, [video.id]: true }));
          } else {
            video.pause();
            setPlayingVideos((prev) => ({ ...prev, [video.id]: false }));
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(videoRefs.current).forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => {
      Object.values(videoRefs.current).forEach((video) => {
        if (video) observer.unobserve(video);
      });
    };
  }, [article]);

  const handlePlayPause = (videoId) => {
    const video = videoRefs.current[videoId];
    if (video) {
      if (playingVideos[videoId]) {
        video.pause();
      } else {
        video.play().catch((error) => console.error('Error playing video:', error));
      }
      setPlayingVideos((prev) => ({ ...prev, [videoId]: !prev[videoId] }));
    }
  };

  const getFirstTextSection = (article) => {
    // If no sections exist, return first 250 chars of main content
    if (!article?.sections?.length) {
      const mainContent = article?.content || '';
      return mainContent.length > 120 ? mainContent.slice(0, 120) + '...' : mainContent;
    }
  
    // Find the first text section
    const firstTextSection = article.sections.find(section => section.type === 'text');
    
    // If no text section found, return empty string
    if (!firstTextSection?.content) return '';
    
    // Strip HTML tags but preserve line breaks and spaces
    const stripHtml = (html) => {
      // Replace line breaks with spaces
      const withLineBreaks = html.replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ');
      // Remove all HTML tags
      const withoutTags = withLineBreaks.replace(/<[^>]*>/g, '');
      // Remove multiple spaces and trim
      return withoutTags.replace(/\s+/g, ' ').trim();
    };
  
    // Get plain text content
    const plainText = stripHtml(firstTextSection.content);
    
    // Return first 250 chars
    return plainText.length > 120 ? plainText.slice(0, 120) + '...' : plainText;
  };

  const renderContent = (content) => {
    if (typeof content === 'string') {
      return content.split(',').map((item) => item.trim());
    }
    return Array.isArray(content) ? content : [content];
  };

//   if (loading)
//   return (
//     <div className="loading-container">
//       <div className="spinner"></div>
//       <p>Loading articles...</p>
//     </div>
//   );
// if (error)
//   return (
//     <div className="error-container">
//       <div className="error-icon">⚠️</div>
//       <p>Error: {error}</p>
//     </div>
//   );

const handleShareButtonClick = (postId) => {
  // Toggle the popup
  setActivePopupPostId(prevId => prevId === postId ? null : postId);
  // Ensure hover is also set to allow persistent display
  setHoverPostId(postId);
};

const handleCopyLink = (postId) => {
  const articleUrl = `https://yourwebsite.com/article/${postId}`;
  
  // Copy to clipboard
  navigator.clipboard.writeText(articleUrl).then(() => {
    // Set copied state and reset after 2 seconds
    setCopiedPostId(postId);
    setTimeout(() => setCopiedPostId(null), 2000);
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};

const handleSocialShare = async (postId, socialPlatform) => {
  try {
    // Increment local share count immediately
    setLocalShareCounts(prev => ({
      ...prev,
      [postId]: (prev[postId] || 0) + 1
    }));

    // Increment share count in Firestore
    await incrementShareCount(postId);

    // Open the share dialog for the specific social platform
    let shareUrl = '';
    const articleUrl = `https://yourwebsite.com/article/${postId}`;
    
    switch(socialPlatform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}`;
        break;
      case 'instagram':
        // Note: Instagram doesn't allow direct sharing via web
        alert('Instagram sharing is not directly supported. Please copy the link.');
        return;
      case 'pinterest':
        shareUrl = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(articleUrl)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank');
  } catch (error) {
    console.error("Error sharing post:", error);
  }
};


if (loading) {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      <p className="ml-4 text-lg">Loading article...</p>
    </div>
  );
}



  const ImageArray = ({ images, layout = 'single' }) => {
    if (!images || images.length === 0) return null;
  
    return (
      <div className={`grid gap-4 ${layout === 'double' ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {images.map((imageUrl, index) => (
          <div 
            key={index} 
            className="relative overflow-hidden aspect-video"
          >
            <img
              src={imageUrl}
              alt={`Gallery image ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="article_details">
      <div className="article__details-title section__padding-case-title">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="article__details-title-big" style={{ display: 'flex', flexDirection: 'column' }}>
            <h1 style={{ color: '#000000', fontFamily: "Poppins-SemiBold", fontSize: "clamp(28px, 4vw, 38px)" }}>
              <span>{article.title || 'No Title'}</span>
            </h1>
            
            <div className="article__details-title-actions" style={{ display: 'flex', alignItems: 'center' }}>
              <h2 style={{ color: '#000', marginBottom: '0' }}>{article.category || 'No Subtitle'}</h2>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="issue-details__social-link-art"
                style={{ color: '#CD3333', marginLeft: '10px' }}
              >
                <FaFacebook />
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="issue-details__social-link-art"
                style={{ color: '#CD3333', marginLeft: '10px' }}
              >
                <FaTwitter />
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="issue-details__social-link-art"
                style={{ color: '#CD3333', marginLeft: '10px' }}
              >
                <FaInstagram />
              </a>
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="issue-details__social-link-art"
                style={{ color: '#CD3333', marginLeft: '10px' }}
              >
                <FaPinterest />
              </a>
              <a
                href="#"
                className={`issue-details__social-link-art`}
                style={{ color: '#CD3333', marginLeft: '10px' }}
              >
                <FaCopy />
              </a>
            </div>
          </div>
          {/*  */}
        </div>
      </div>


      {article.coverMedia && (
        <div className="article__details-images" style={{ padding: '0' }}>
          <div className="single-image-section">
            {article.coverMedia.type === 'video' ? (
              <video
                ref={(el) => (videoRefs.current['cover'] = el)}
                id="cover"
                loop
                autoPlay
                muted
                playsInline
                onClick={() => handlePlayPause('cover')}
                style={{ cursor: 'pointer' }}
              >
                <source src={article.coverMedia.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <BlurhashImg src={article.coverMedia.url} placeholderColor="#888" />
            )}
          </div>
        </div>
      )}

      <p className='covermedia-p'>image by Qingyan Zhu | courtesy of HCCH Studio</p>

      {article.sections && article.sections.length > 0 && article.sections.map((section, index) => (
        <div key={index} className="article-more-details" id={`section-${index}`}>
          {section.type === 'text' && (
            <div className="ulev_morePD-contain sectiona__padding">
              <p className='ulev_morePD-other'></p>
              <div className='ulev_morePD-p'>
                <div 
                  dangerouslySetInnerHTML={{ __html: section.content }} 
                  className="ulev_morePD-content"
                  style={{color: "#000"}}
                />
              </div>
            </div>
          )}
          {section.type === 'image' && section.layout !== 'double' && (
            <div className="article__details-images image__padding" style={{ padding: '0rem 4.2rem 0rem 4.2rem' }}>
              {renderContent(section.content).map((url, idx) => (
                <div key={idx} className="single-image-section">
                  <BlurhashImg src={url} placeholderColor="#888" />
                </div>
              ))}
            </div>
          )}
          {section.type === 'image_pair' && (
            <div className="article__details-image-pair section__padding">
              {renderContent(section.content).map((url, idx) => (
                <div key={idx} className="half-image-section">
                  <BlurhashImg src={url} placeholderColor="#888" />
                </div>
              ))}
            </div>
          )}
          {section.type === 'video' && section.layout === 'double' && (
            <div className="article__details-images section__padding article__details-images-double">
              {renderContent(section.content).map((url, idx) => (
                <div key={idx} className="single__image-section">
                  <video
                    ref={(el) => (videoRefs.current[`video-${index}-${idx}`] = el)}
                    id={`video-${index}-${idx}`}
                    loop
                    autoPlay
                    muted
                    playsInline
                    onClick={() => handlePlayPause(`video-${index}-${idx}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <source src={url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ))}
            </div>
          )}
          {section.type === 'video' && section.layout !== 'double' && (
            <div className="article__details-images" style={{ padding: '4rem 4rem 4rem 4rem' }}>
              {renderContent(section.content).map((url, idx) => (
                <div key={idx} className="single__image-section">
                  <video
                    ref={(el) => (videoRefs.current[`video-${index}-${idx}`] = el)}
                    id={`video-${index}-${idx}`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onClick={() => handlePlayPause(`video-${index}-${idx}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <source src={url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ))}
            </div>
          )}
          {section.type === 'video_pair' && (
            <div className="article-details-video-pair section__padding-vid">
              {renderContent(section.content).map((url, idx) => (
                <div key={idx} className="half-video-section">
                  <video
                    ref={(el) => (videoRefs.current[`video-pair-${index}-${idx}`] = el)}
                    id={`video-pair-${index}-${idx}`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onClick={() => handlePlayPause(`video-pair-${index}-${idx}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <source src={url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ))}
            </div>
          )}
          {section.type === 'image_array' && (
            <ImageArray 
              images={section.images}
              layout={section.layout}
            />
          )}
        </div>
      ))}

      <div className="ulev_morePD-contain sectiona__padding">
        <p className='ulev_morePD-other'></p>
        <div className='ulev_morePD-p'>
          <div 
            className="ulev_morePD-content"
            style={{color: "#000", display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",}}
          >
            <div
              className="project-info-content"
              style={{
                flex: "2",
              }}
            >
              <h2
                style={{
                  fontFamily: "Syne",
                  color: "#CD3333",
                  fontSize: "clamp(20px, 4vw, 20px)",
                  fontWeight: "600",
                }}
              >
                Project Info
              </h2>
              <br />
              <div className="project-info-details">
                <div className="project-info-item">
                  <h3>
                    <strong>Name:</strong> Starry Night Retreat | starryretreat.bh
                  </h3>
                </div>
                <div className="project-info-item">
                  <h3>
                    <strong>Landscape Concept:</strong> Halim Zulkic
                  </h3>
                </div>
                <div className="project-info-item">
                  <h3>
                    <strong>Location:</strong> Luza, Visoko, Bosnia and Herzegovina
                  </h3>
                </div>
                <br />
                <div className="project-info-item">
                  <h3>
                    <strong>Photography:</strong> Almin Tabak | alminjum, Anel Uzunovic |
                    uzunamel, thamali.tsinpou | designgona
                  </h3>
                </div>
              </div>
            </div>

            {/* Social Icons */}
            <div
              className="project-info-social-icons"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "1rem",
                marginTop: "auto",
                color: "#CD3333",
              }}
            >
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FaInstagram size={24} />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FaTwitter size={24} />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FaPinterest size={24} />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FaTwitter size={24} />
              </a>
            </div>
          </div>
        </div>
      </div>     

      {/* Move Related Articles section outside of the sections mapping */}
      {article.relatedProjects && article.relatedProjects.length > 0 && (
        // <div className="section__padding">
        //   <h2 style={{
        //     fontFamily: "Poppins-SemiBold", 
        //     color: "#CD3333", 
        //     fontSize: "clamp(28px, 4vw, 36px)", 
        //     fontWeight: "600"
        //   }}>
        //     Related Articles
        //   </h2>

        //   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        //     {article.relatedProjects.map((relatedProject, index) => (
        //       <Link 
        //         key={index} 
        //         to={`/article/${relatedProject.id}`} 
        //         className="mb-8 cursor-pointer duration-300"
        //       >
        //         <div>
        //           <div className="h-[300px] w-full overflow-hidden">
        //             <BlurhashImg
        //               src={relatedProject.coverImage}
        //               alt={relatedProject.title}
        //               className="w-full h-full object-cover responsive blur_load"
        //               loading="lazy"
        //               placeholderColor="#888"
        //               onError={(e) => {
        //                 console.error('Error loading image:', relatedProject.coverImage);
        //                 e.target.src = '/fallback-image.jpg';
        //               }}
        //             />
        //           </div>
        //           <div className="mt-4">
        //             <p style={{
        //               fontFamily: "Poppins-Regular",
        //               color: "#CD3333",
        //               fontSize: "16px"
        //             }}>
        //               {relatedProject.category || 'Project'}
        //             </p>
        //             <h3 style={{
        //               fontFamily: "Poppins-SemiBold",
        //               fontSize: "26px",
        //               fontWeight: "600",
        //               lineHeight: "1.2",
        //               paddingTop: '6px'
        //             }}>
        //               {relatedProject.title}
        //             </h3>
        //             <p
        //               dangerouslySetInnerHTML={{ 
        //                 __html: (() => {
        //                   const content = getFirstTextSection(article);
        //                   if (!content) return 'No content available';
        //                   return content.length > 250 ? content.slice(0, 250) + '...' : content;
        //                 })()
        //               }}
        //               style={{
        //                 fontFamily: "Poppins-Light", 
        //                 lineHeight: "1.3", 
        //                 color: "#000000", 
        //                 fontSize: '14px', 
        //                 paddingTop: '6px'
        //               }}
        //             ></p>
        //           </div>
        //         </div>
        //       </Link>
        //     ))}
        //   </div>
        // </div>

        // {article.relatedProjects && article.relatedProjects.length > 0 && (
          <div className="section__padding">
            <h2 style={{
              fontFamily: "Poppins-SemiBold", 
              color: "#CD3333", 
              fontSize: "clamp(28px, 4vw, 36px)", 
              fontWeight: "600"
            }}>
              Related Articles
            </h2>
        
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {article.relatedProjects.map((relatedProject, index) => (
                <Link 
                  key={index} 
                  to={`/article/${relatedProject.id}`} 
                  className="mb-8 cursor-pointer duration-300"
                >
                  <div>
                    <div className="h-[300px] w-full overflow-hidden">
                      <BlurhashImg
                        src={relatedProject.coverImage}
                        alt={relatedProject.title}
                        className="w-full h-full object-cover responsive blur_load"
                        id={`clickableImage${relatedProject.id}`}
                        loading="lazy" 
                        placeholderColor="#888"
                        onError={(e) => {
                          console.error('Error loading image:', relatedProject.coverImage);
                          e.target.src = '/fallback-image.jpg';
                        }}
                      />
                    </div>
                    <div className="mt-4">
                      <p style={{
                        fontFamily: "Poppins-Regular",
                        color: "#CD3333",
                        fontSize: "16px"
                      }}>
                        {relatedProject.category}
                      </p>
                      <h3 style={{
                        fontFamily: "Poppins-SemiBold",
                        fontSize: "26px",
                        fontWeight: "600",
                        lineHeight: "1.2",
                        paddingTop: '6px'
                      }}>
                        {relatedProject.title}
                      </h3>
                      <div 
                        style={{
                          fontFamily: "Poppins-Light",
                          lineHeight: "1.3",
                          color: "#000000",
                          fontSize: '14px',
                          paddingTop: '6px'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: (() => {
                            const content = getFirstTextSection(article);
                            if (!content) return 'No content available';
                            return content.length > 250 ? content.slice(0, 250) + '...' : content;
                          })()
                        }} 
                      />
                    </div>
                    <div className="flex items-center mt-2 relative">
                      <p style={{
                        fontFamily: "Poppins-Light",
                        fontSize: "16px",
                        color: "#CD3333",
                      }}>
                        {new Date(relatedProject.createdAt?.seconds * 1000).toLocaleDateString()}
                      </p>
                      <span className="ml-12" style={{color: "#CD3333"}}>
                        {/* {localShareCounts[relatedProject.id] || relatedProject.shareCount || 0} */}
                      </span>
                      <button 
                        ref={(el) => {
                          if (el) {
                            if (!shareRefsMap.current[relatedProject.id]) {
                              shareRefsMap.current[relatedProject.id] = {};
                            }
                            shareRefsMap.current[relatedProject.id].buttonRef = el;
                          }
                        }}
                        className="share-button ml-2"
                        onClick={(e) => {
                          e.stopPropagation(); 
                          e.preventDefault(); 
                          handleShareButtonClick(relatedProject.id);
                        }}
                        style={{color: "#CD3333"}}
                      >
                        Share
                      </button>
        
                      {(hoverPostId === relatedProject.id || activePopupPostId === relatedProject.id) && (
                        <div 
                          ref={(el) => {
                            if (el) {
                              if (!shareRefsMap.current[relatedProject.id]) {
                                shareRefsMap.current[relatedProject.id] = {};
                              }
                              shareRefsMap.current[relatedProject.id].popupRef = el;
                            }
                          }}
                          className="social-popup absolute z-10 bg-white border border-gray-200 p-3"
                          onClick={(e) => e.stopPropagation()} 
                        >
                          <a
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="issue-details__social-link-feed"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSocialShare(relatedProject.id, 'facebook');
                            }}
                          >
                            <FaFacebook />
                          </a>
                          <a
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="issue-details__social-link-feed"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSocialShare(relatedProject.id, 'twitter');
                            }}
                          >
                            <FaTwitter />
                          </a>
                          <a
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="issue-details__social-link-feed"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSocialShare(relatedProject.id, 'instagram');
                            }}
                          >
                            <FaInstagram />
                          </a>
                          <a
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="issue-details__social-link-feed"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSocialShare(relatedProject.id, 'pinterest');
                            }}
                          >
                            <FaPinterest />
                          </a>
                          <a
                            href="#"
                            className={`issue-details__social-link-feed ${
                              copiedPostId === relatedProject.id ? 'text-green-500' : ''
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCopyLink(relatedProject.id);
                            }}
                            title={copiedPostId === relatedProject.id ? 'Copied!' : 'Copy Link'}
                          >
                            {copiedPostId === relatedProject.id ? '✓ Copied' : <FaCopy />}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      {/* )} */}
    </div>
  );
};

export default ArticleDetails;












// import React, { useState, useEffect, useRef } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import { collection, doc, getDoc } from 'firebase/firestore';
// import { db } from '../../firebase';
// import './articleDetails.css';
// import { FaFacebook, FaTwitter, FaInstagram, FaPinterest, FaCopy } from 'react-icons/fa';
// import BlurhashImg from '../../@/components/Image/BlurhashImg';

// const ArticleDetails = () => {
//   const [article, setArticle] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const { id } = useParams();
//   const videoRefs = useRef({});
//   const [playingVideos, setPlayingVideos] = useState({});

//   useEffect(() => {
//     const fetchArticle = async () => {
//       if (!id) {
//         setError('No article ID provided');
//         setLoading(false);
//         return;
//       }

//       try {
//         setLoading(true);
//         console.log('Fetching article with ID:', id);
        
//         const docRef = doc(db, 'news', id);
//         const docSnap = await getDoc(docRef);
        
//         console.log('Document snapshot:', docSnap);

//         if (docSnap.exists()) {
//           const articleData = { id: docSnap.id, ...docSnap.data() };
//           console.log('Article data:', articleData);
//           setArticle(articleData);
//         } else {
//           throw new Error('No article found for this ID!');
//         }
//       } catch (error) {
//         console.error('Error fetching article:', error);
//         setError(error.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchArticle();
//   }, [id]);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       (entries) => {
//         entries.forEach((entry) => {
//           const video = entry.target;
//           if (entry.isIntersecting) {
//             video.play().catch((error) => console.error('Error playing video:', error));
//             setPlayingVideos((prev) => ({ ...prev, [video.id]: true }));
//           } else {
//             video.pause();
//             setPlayingVideos((prev) => ({ ...prev, [video.id]: false }));
//           }
//         });
//       },
//       { threshold: 0.5 }
//     );

//     Object.values(videoRefs.current).forEach((video) => {
//       if (video) observer.observe(video);
//     });

//     return () => {
//       Object.values(videoRefs.current).forEach((video) => {
//         if (video) observer.unobserve(video);
//       });
//     };
//   }, [article]);

//   const handlePlayPause = (videoId) => {
//     const video = videoRefs.current[videoId];
//     if (video) {
//       if (playingVideos[videoId]) {
//         video.pause();
//       } else {
//         video.play().catch((error) => console.error('Error playing video:', error));
//       }
//       setPlayingVideos((prev) => ({ ...prev, [videoId]: !prev[videoId] }));
//     }
//   };

//   const getFirstTextSection = (article) => {
//     if (!article?.sections) return article?.content || '';
//     const firstTextSection = article.sections.find(section => section.type === 'text');
//     return firstTextSection?.content || article?.content || '';
//   };

//   const renderContent = (content) => {
//     if (typeof content === 'string') {
//       return content.split(',').map((item) => item.trim());
//     }
//     return Array.isArray(content) ? content : [content];
//   };

//   const ImageArray = ({ images, layout = 'single' }) => {
//     if (!images || images.length === 0) return null;
  
//     return (
//       <div className={`grid gap-4 ${layout === 'double' ? 'grid-cols-2' : 'grid-cols-1'}`}>
//         {images.map((imageUrl, index) => (
//           <div 
//             key={index} 
//             className="relative overflow-hidden aspect-video"
//           >
//             <img
//               src={imageUrl}
//               alt={`Gallery image ${index + 1}`}
//               className="w-full h-full object-cover"
//             />
//           </div>
//         ))}
//       </div>
//     );
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center min-h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
//         <p className="ml-4 text-lg">Loading article...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex flex-col items-center justify-center min-h-screen text-red-600">
//         <div className="text-4xl mb-4">⚠️</div>
//         <p className="text-lg">Error: {error}</p>
//         <Link to="/" className="mt-4 text-blue-600 hover:underline">
//           Return to Home
//         </Link>
//       </div>
//     );
//   }

//   if (!article) {
//     return (
//       <div className="flex flex-col items-center justify-center min-h-screen">
//         <p className="text-lg">No article found</p>
//         <Link to="/" className="mt-4 text-blue-600 hover:underline">
//           Return to Home
//         </Link>
//       </div>
//     );
//   }

//   return (
//     <div className="article_details">
//       <div className="article__details-title section__padding-case-title">
//         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
//           <div className="article__details-title-big" style={{ display: 'flex', flexDirection: 'column' }}>
//             <h1 style={{ color: '#000000', fontFamily: "Poppins-SemiBold", fontSize: "clamp(28px, 4vw, 38px)" }}>
//               <span>{article?.title || 'No Title'}</span>
//             </h1>
            
//             <div className="article__details-title-actions" style={{ display: 'flex', alignItems: 'center' }}>
//               <h2 style={{ color: '#000', marginBottom: '0' }}>{article?.category || 'No Category'}</h2>
//               <div className="flex gap-2 ml-4">
//                 {[FaFacebook, FaTwitter, FaInstagram, FaPinterest, FaCopy].map((Icon, index) => (
//                   <button
//                     key={index}
//                     className="issue-details__social-link-art hover:opacity-75 transition-opacity"
//                     style={{ color: '#CD3333' }}
//                     onClick={(e) => {
//                       e.preventDefault();
//                       // Add your social share logic here
//                     }}
//                   >
//                     <Icon />
//                   </button>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {article?.coverMedia && (
//         <div className="article__details-images" style={{ padding: '0' }}>
//           <div className="single-image-section">
//             {article.coverMedia.type === 'video' ? (
//               <video
//                 ref={(el) => (videoRefs.current['cover'] = el)}
//                 id="cover"
//                 loop
//                 muted
//                 playsInline
//                 onClick={() => handlePlayPause('cover')}
//                 className="w-full cursor-pointer"
//               >
//                 <source src={article.coverMedia.url} type="video/mp4" />
//                 Your browser does not support the video tag.
//               </video>
//             ) : (
//               <BlurhashImg 
//                 src={article.coverMedia.url} 
//                 placeholderColor="#888" 
//                 className="w-full"
//                 alt={article.title}
//               />
//             )}
//           </div>
//         </div>
//       )}

//       {article?.imageCredit && (
//         <p className='covermedia-p text-sm text-gray-600 italic mt-2'>
//           image by {article.imageCredit}
//         </p>
//       )}

//       {article?.sections && article.sections.length > 0 && article.sections.map((section, index) => (
//         <div key={index} className="article-more-details" id={`section-${index}`}>
//           {section.type === 'text' && (
//             <div className="ulev_morePD-contain sectiona__padding">
//               <div className='ulev_morePD-p'>
//                 <div 
//                   dangerouslySetInnerHTML={{ __html: section.content }} 
//                   className="ulev_morePD-content"
//                   style={{color: "#000"}}
//                 />
//               </div>
//             </div>
//           )}
          
//           {section.type === 'image' && section.layout !== 'double' && (
//             <div className="article__details-images image__padding">
//               {renderContent(section.content).map((url, idx) => (
//                 <div key={idx} className="single-image-section">
//                   <BlurhashImg src={url} placeholderColor="#888" />
//                 </div>
//               ))}
//             </div>
//           )}

//           {section.type === 'image_pair' && (
//             <div className="article__details-image-pair section__padding">
//               {renderContent(section.content).map((url, idx) => (
//                 <div key={idx} className="half-image-section">
//                   <BlurhashImg src={url} placeholderColor="#888" />
//                 </div>
//               ))}
//             </div>
//           )}

//           {section.type === 'video' && (
//             <div className={`article__details-images section__padding ${
//               section.layout === 'double' ? 'article__details-images-double' : ''
//             }`}>
//               {renderContent(section.content).map((url, idx) => (
//                 <div key={idx} className="single__image-section">
//                   <video
//                     ref={(el) => (videoRefs.current[`video-${index}-${idx}`] = el)}
//                     id={`video-${index}-${idx}`}
//                     loop
//                     muted
//                     playsInline
//                     onClick={() => handlePlayPause(`video-${index}-${idx}`)}
//                     className="w-full cursor-pointer"
//                   >
//                     <source src={url} type="video/mp4" />
//                     Your browser does not support the video tag.
//                   </video>
//                 </div>
//               ))}
//             </div>
//           )}

//           {section.type === 'image_array' && (
//             <ImageArray 
//               images={section.images}
//               layout={section.layout}
//             />
//           )}
//         </div>
//       ))}

//       <div className="ulev_morePD-contain sectiona__padding">
//         {article?.projectInfo && (
//           <div className='ulev_morePD-p'>
//             <div 
//               className="ulev_morePD-content"
//               style={{
//                 color: "#000",
//                 display: "flex",
//                 flexDirection: "row",
//                 justifyContent: "flex-end",
//                 alignItems: "flex-start",
//                 gap: "2rem"
//               }}
//             >
//               <div className="project-info-content flex-grow">
//                 <h2 className="text-[#CD3333] text-xl font-semibold font-syne mb-4">
//                   Project Info
//                 </h2>
//                 <div className="space-y-2">
//                   {Object.entries(article.projectInfo).map(([key, value]) => (
//                     <div key={key} className="project-info-item">
//                       <h3>
//                         <strong>{key}:</strong> {value}
//                       </h3>
//                     </div>
//                   ))}
//                 </div>
//               </div>

//               <div className="project-info-social-icons flex gap-4 text-[#CD3333]">
//                 {[FaInstagram, FaTwitter, FaPinterest, FaFacebook].map((Icon, index) => (
//                   <button
//                     key={index}
//                     className="hover:opacity-75 transition-opacity"
//                     onClick={(e) => {
//                       e.preventDefault();
//                       // Add your social share logic here
//                     }}
//                   >
//                     <Icon size={24} />
//                   </button>
//                 ))}
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {article?.relatedProjects && article.relatedProjects.length > 0 && (
//         <div className="section__padding">
//           <h2 className="text-[#CD3333] text-3xl font-semibold font-poppins mb-6">
//             Related Articles
//           </h2>

//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//             {article.relatedProjects.map((project, index) => (
//               <Link 
//                 key={index} 
//                 to={`/work/${project.slug}`} 
//                 className="group transition-transform duration-300 hover:-translate-y-1"
//               >
//                 <div className="h-[300px] overflow-hidden mb-4">
//                   <BlurhashImg
//                     src={project.coverImage}
//                     alt={project.title}
//                     className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
//                     loading="lazy"
//                     placeholderColor="#888"
//                   />
//                 </div>
//                 <div>
//                   <p className="text-[#CD3333] text-sm font-poppins">
//                     {project.category || 'Project'}
//                   </p>
//                   <h3 className="text-2xl font-semibold mt-2 mb-3">
//                     {project.title}
//                   </h3>
//                   <p className="text-sm line-clamp-3">
//                     {getFirstTextSection(project)}
//                   </p>
//                 </div>
//               </Link>
//             ))}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ArticleDetails;